import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTheme } from '@mui/material';
import { useRecoilValue } from 'recoil';
import {
  hasInternalDashAccessState,
  userTraceV2EnabledState,
} from '../../states';
import { OIEntity, ProductType, TraceConfig, ZoomData } from '../../types';
import { ColorMode, HeatmapColorSettings, themeForMode } from '../../theme';
import { dayjs, ET } from '../../util/shared/date';
import { safeMerge } from '../../util';
import { greekForLense } from '../../util/oi';
import {
  HEATMAP_FIRST_AVAILABLE_DATE,
  TraceShowChartType,
} from '../../config/oi';
import { useTraceSize } from '../../hooks/trace/useTraceSize';
import { TraceContext, TraceContextValue, TraceMode } from './TraceContext';

const INTERNAL_VALID_SYMS = new Set(['SPX', 'VIX']);

interface TraceProviderProps {
  config: TraceConfig;
  onConfigChange: (patch: Partial<TraceConfig>) => void;
  /**
   * Where this Trace is mounted. Defaults to 'standalone'. Canvas tiles pass
   * 'canvas' to suppress chrome that's redundant with Canvas's own (fullscreen
   * button, etc.).
   */
  mode?: TraceMode;
  /** Hosting product; only `INTERNAL_OPEN_INTEREST` surfaces the internal lenses. */
  productType?: ProductType;
  /**
   * Optionally lift the view selection to the host (controlled); Canvas uses
   * this so the choice survives the tile remount on resize. Standalone omits it.
   */
  showChartType?: TraceShowChartType;
  onShowChartTypeChange?: (next: TraceShowChartType) => void;
  children: ReactNode;
}

export const TraceProvider = ({
  config,
  onConfigChange,
  mode = 'standalone',
  productType,
  showChartType: controlledShowChartType,
  onShowChartTypeChange,
  children,
}: TraceProviderProps) => {
  const theme = useTheme();
  const userTraceV2Enabled = useRecoilValue(userTraceV2EnabledState);
  const isInternalUser = useRecoilValue(hasInternalDashAccessState);
  // Internal lenses only show on /oi for internal users.
  const showInternalLenses =
    isInternalUser && productType === ProductType.INTERNAL_OPEN_INTEREST;

  const rootRef = useRef<HTMLDivElement>(null);
  const size = useTraceSize(rootRef);

  const [fullscreen, setFullscreen] = useState(false);
  const [internalShowChartType, setInternalShowChartType] =
    useState<TraceShowChartType>(TraceShowChartType.Both);
  // Controlled when the host supplies a value+callback (Canvas); otherwise the
  // provider owns the state (standalone).
  const isShowChartTypeControlled = controlledShowChartType != null;
  const showChartType = isShowChartTypeControlled
    ? controlledShowChartType
    : internalShowChartType;
  const setShowChartType = useCallback(
    (next: TraceShowChartType) => {
      onShowChartTypeChange?.(next);
      if (!isShowChartTypeControlled) {
        setInternalShowChartType(next);
      }
    },
    [onShowChartTypeChange, isShowChartTypeControlled],
  );
  const [zoomData, setZoomData] = useState<ZoomData | undefined>();

  // v2 → v1 rollback: when v2 entitlement is lost mid-session, revert
  // the v1 defaults for actor/inversion (same behavior as Trace.tsx today).
  useEffect(() => {
    if (!userTraceV2Enabled) {
      const patch: Partial<TraceConfig> = {};
      if (config.mktActor !== OIEntity.CUST) {
        patch.mktActor = OIEntity.CUST;
      }
      if (!config.inverted) {
        patch.inverted = true;
      }
      if (Object.keys(patch).length > 0) {
        onConfigChange(patch);
      }
    }
    // One-shot correction when v2 flips off — don't enforce continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTraceV2Enabled]);

  // showChartType is plain user state. Components that need to render
  // differently per breakpoint read `isMobile` / `isTablet` / `isDesktop`
  // from context and adapt locally — we don't mutate showChartType here.

  // Internal users can switch between SPX and VIX; everyone else is SPX-only.
  const intradaySym = useMemo(() => {
    if (!isInternalUser) {
      return 'SPX';
    }
    const s = (config.sym ?? '').toUpperCase();
    return INTERNAL_VALID_SYMS.has(s) ? s : 'SPX';
  }, [isInternalUser, config.sym]);

  const intradayDate = useMemo(() => {
    const parsed = dayjs.tz(config.date, ET);
    if (
      !config.date ||
      !parsed.isValid() ||
      parsed.isBefore(HEATMAP_FIRST_AVAILABLE_DATE, 'date')
    ) {
      return dayjs.tz(dayjs().format('YYYY-MM-DD'), ET);
    }
    return parsed;
  }, [config.date]);

  const selectedLense = config.lense;
  const selectedGreek = useMemo(
    () => greekForLense(selectedLense),
    [selectedLense],
  );

  const selectedTheme = useMemo(
    () => (config.useWhiteMode ? themeForMode(ColorMode.LIGHT) : theme),
    [config.useWhiteMode, theme],
  );

  const heatmapColorSettings = useMemo<HeatmapColorSettings>(() => {
    const base = selectedTheme.palette.trace.heatmapSettings;
    const perLense = config.customColorsPerLense?.[selectedLense] ?? {};
    const customSettings: Partial<HeatmapColorSettings> = {
      candleDownFillColor: config.downCandleFill,
      candleUpFillColor: config.upCandleFill,
      posHeatmapColor: perLense.posHeatmapColor ?? config.posColor,
      negHeatmapColor: perLense.negHeatmapColor ?? config.negColor,
      posStrikeColor: config.posStrikeColor,
      negStrikeColor: config.negStrikeColor,
      putColor: config.putColor,
      callColor: config.callColor,
      candleUpLineColor: config.upCandleLineColor,
      candleDownLineColor: config.downCandleLineColor,
      hiroColor: config.hiroColor,
      contourLineColor: config.contourLineColor,
    };
    return safeMerge(base, customSettings) as HeatmapColorSettings;
  }, [selectedTheme, selectedLense, config]);

  // Fullscreen forces color scale off, preserving the current auto-hide
  // behavior of oiShowColorScaleState.
  const effectiveConfig = useMemo<TraceConfig>(() => {
    if (fullscreen && config.showColorScale) {
      return { ...config, showColorScale: false };
    }
    return config;
  }, [config, fullscreen]);

  const value = useMemo<TraceContextValue>(
    () => ({
      config: effectiveConfig,
      updateConfig: onConfigChange,
      userTraceV2Enabled,
      isInternalUser,
      showInternalLenses,
      mode,
      containerWidth: size.width,
      containerHeight: size.height,
      breakpoint: size.breakpoint,
      isMobile: size.breakpoint === 'mobile',
      isTablet: size.breakpoint === 'tablet',
      isDesktop: size.breakpoint === 'desktop',
      intradayDate,
      intradaySym,
      selectedGreek,
      selectedLense,
      selectedTheme,
      heatmapColorSettings,
      fullscreen,
      setFullscreen,
      showChartType,
      setShowChartType,
      zoomData,
      setZoomData,
    }),
    [
      effectiveConfig,
      onConfigChange,
      userTraceV2Enabled,
      isInternalUser,
      showInternalLenses,
      mode,
      size.width,
      size.height,
      size.breakpoint,
      intradayDate,
      intradaySym,
      selectedGreek,
      selectedLense,
      selectedTheme,
      heatmapColorSettings,
      fullscreen,
      showChartType,
      setShowChartType,
      zoomData,
    ],
  );

  return (
    <TraceContext.Provider value={value}>
      <div
        ref={rootRef}
        style={{
          width: '100%',
          height: '100%',
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </TraceContext.Provider>
  );
};
